<template>
  <svg
    class="ishikawa-diagram"
    viewBox="0 0 1000 460"
    preserveAspectRatio="xMidYMid meet"
    role="img"
    :aria-label="name"
  >
    <!-- tail -->
    <path
      class="ishikawa-diagram__tail"
      :d="`M0,${spineY - 46} L${tailX + 24},${spineY} L0,${spineY + 46} L${tailX - 14},${spineY} Z`"
    />

    <!-- spine -->
    <line class="ishikawa-diagram__spine" :x1="tailX" :y1="spineY" :x2="headBaseX" :y2="spineY" />

    <!-- head -->
    <path
      class="ishikawa-diagram__head"
      :d="`M${headBaseX},${spineY - 96} L${headTipX},${spineY} L${headBaseX},${spineY + 96} Z`"
    />
    <circle class="ishikawa-diagram__eye" :cx="headBaseX + 36" :cy="spineY - 42" r="6" />
    <foreignObject :x="headBaseX + 8" :y="spineY - 35" width="144" height="70">
      <div xmlns="http://www.w3.org/1999/xhtml" class="ishikawa-diagram__problem-wrap">
        <span class="ishikawa-diagram__problem">
          {{ truncatedProblem }}
        </span>
      </div>
    </foreignObject>

    <!-- ribs -->
    <g v-for="rib in ribs" :key="`${rib.category.type}-${rib.side}`">
      <line
        class="ishikawa-diagram__rib"
        :x1="rib.labelAnchor.x"
        :y1="rib.labelAnchor.y"
        :x2="rib.junction.x"
        :y2="rib.junction.y"
        :style="{ '--rib-color': rib.category.color }"
      />

      <g v-for="(tick, index) in rib.ticks" :key="index">
        <line
          class="ishikawa-diagram__tick"
          :x1="tick.lineStartX"
          :y1="tick.point.y"
          :x2="tick.point.x"
          :y2="tick.point.y"
          :style="{ '--rib-color': rib.category.color }"
        />
        <circle
          class="ishikawa-diagram__tick-dot"
          :cx="tick.point.x"
          :cy="tick.point.y"
          r="4.5"
          :class="{ 'ishikawa-diagram__tick-dot--selected': tick.reason.selected }"
          :style="{ '--rib-color': rib.category.color }"
        />
        <foreignObject
          :x="tick.labelBox.x"
          :y="tick.point.y - 10"
          :width="tick.labelBox.width"
          height="20"
          overflow="hidden"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            class="ishikawa-diagram__tick-label"
            :title="tick.reason.name"
          >
            {{ tick.reason.name }}
          </div>
        </foreignObject>
      </g>

      <text
        v-if="rib.overflowCount > 0"
        class="ishikawa-diagram__overflow"
        :x="rib.overflowPoint.x"
        :y="rib.overflowPoint.y"
        text-anchor="middle"
      >
        {{
          t("modules.sqdc.components.eventActionBoard.modals.SQDCIshikawaField.moreCauses", {
            count: rib.overflowCount,
          })
        }}
      </text>

      <foreignObject
        :x="rib.labelBox.x"
        :y="rib.labelBox.y"
        :width="rib.labelBox.width"
        :height="rib.labelBox.height"
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          class="ishikawa-diagram__category"
          :style="{ '--rib-color': rib.category.color }"
        >
          <span>{{ rib.category.label }}</span>
        </div>
      </foreignObject>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed } from "vue";
import type { ReasonType } from "./SQDCIshikawaDraft";
import { getIshikawaCategories, type IshikawaCategoryMeta } from "./ishikawaCategories";
import type { IshikawaAreaConfig } from "./ishikawaConfig";
import { createDefaultIshikawaConfig } from "./ishikawaConfig";

type DiagramReason = {
  name: string;
  type: ReasonType;
  selected: boolean;
};

const props = defineProps<{
  name: string;
  reasons: DiagramReason[];
  areas?: IshikawaAreaConfig[];
}>();

const spineY = 230;
const tailX = 40;
const headBaseX = 760;
const headTipX = 990;
const maxTicksPerRib = 3;
const causeAreaStartX = 6;
const causeAreaEndX = headBaseX - 40;

const truncatedProblem = computed(() => {
  const problem = props.name.trim();
  return problem.length > 198 ? `${problem.slice(0, 197)}…` : problem;
});

function buildRibs(categories: IshikawaCategoryMeta[], side: "top" | "bottom") {
  const availableWidth = causeAreaEndX - causeAreaStartX;
  const laneWidth = availableWidth / Math.max(categories.length, 1);

  return categories.map((category, index) => {
    const laneStartX = causeAreaStartX + laneWidth * index;
    const laneEndX = causeAreaStartX + laneWidth * (index + 1);
    const ribWidth = Math.min(110, laneWidth * 0.38);
    const junction = {
      x: laneEndX,
      y: spineY,
    };
    const labelAnchor = {
      x: junction.x - ribWidth,
      y: side === "top" ? spineY - 130 : spineY + 130,
    };
    const reasons = props.reasons
      .filter((reason) => reason.type === category.type)
      .sort((a, b) => Number(b.selected) - Number(a.selected));
    const shown = reasons.slice(0, maxTicksPerRib);
    const overflowCount = Math.max(0, reasons.length - maxTicksPerRib);

    const ticks = shown.map((reason, tickIndex) => {
      const t = 0.22 + tickIndex * 0.22;
      const point = {
        x: labelAnchor.x + (junction.x - labelAnchor.x) * t,
        y: labelAnchor.y + (junction.y - labelAnchor.y) * t,
      };
      const tickWidth = Math.min(58, Math.max(18, laneWidth * 0.18));
      const lineStartX = point.x - tickWidth;
      const labelBox = {
        x: laneStartX,
        width: Math.max(0, lineStartX - laneStartX - 6),
      };

      return {
        reason,
        point,
        labelBox,
        lineStartX,
      };
    });

    const categoryWidth = Math.min(156, Math.max(64, laneWidth - 12));
    const labelBox = {
      x: labelAnchor.x - categoryWidth / 2,
      y: side === "top" ? labelAnchor.y - 34 : labelAnchor.y + 6,
      width: categoryWidth,
      height: 28,
    };
    const overflowPoint = {
      x: labelBox.x + labelBox.width / 2,
      y: side === "top" ? labelBox.y - 10 : labelBox.y + labelBox.height + 18,
    };

    return { category, side, junction, labelAnchor, labelBox, overflowPoint, ticks, overflowCount };
  });
}

const categories = computed(() =>
  getIshikawaCategories(props.areas ?? createDefaultIshikawaConfig(t).areas),
);
const ribs = computed(() => {
  const splitIndex = Math.ceil(categories.value.length / 2);
  return [
    ...buildRibs(categories.value.slice(0, splitIndex), "top"),
    ...buildRibs(categories.value.slice(splitIndex), "bottom"),
  ];
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.ishikawa-diagram {
  width: 100%;
  height: auto;
  min-height: 14rem;
}

.ishikawa-diagram__tail,
.ishikawa-diagram__head {
  fill: var(--p-gray-800);
}

.ishikawa-diagram__spine {
  stroke: var(--p-gray-800);
  stroke-width: 5;
  stroke-linecap: round;
}

.ishikawa-diagram__eye {
  fill: var(--p-white);
}

.ishikawa-diagram__problem-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.15rem;
  overflow: hidden;
}

.ishikawa-diagram__problem {
  display: -webkit-box;
  max-height: 100%;
  color: var(--p-white);
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.15;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
}

.ishikawa-diagram__rib {
  stroke: var(--rib-color);
  stroke-width: 3;
}

.ishikawa-diagram__tick {
  stroke: var(--rib-color);
  stroke-width: 1.5;
  opacity: 0.7;
}

.ishikawa-diagram__tick-dot {
  fill: var(--rib-color);
}

.ishikawa-diagram__tick-dot--selected {
  fill: var(--p-yellow-500, var(--p-orange-400));
  stroke: var(--rib-color);
  stroke-width: 1.5;
}

.ishikawa-diagram__tick-label {
  width: 100%;
  overflow: hidden;
  color: var(--p-gray-700);
  font-size: 13px;
  line-height: 20px;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ishikawa-diagram__overflow {
  fill: var(--p-gray-500);
  font-size: 11px;
  font-style: italic;
}

.ishikawa-diagram__category {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 100%;
  border-radius: var(--p-border-radius-md);
  background: var(--rib-color);
  color: var(--p-white);
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0.6rem;
}

.ishikawa-diagram__category i {
  flex-shrink: 0;
}

.ishikawa-diagram__category span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
